import {DetailPanel} from '../../../../components/detailLayout';
import React, {useState} from "react";
import {InspectionReportFieldControl} from "./InspectionReportFieldControl";
import {InspectionReportEditModal} from "./InspectionReportEditModal";
import {InspectionReportLegend, InspectionReportOverviewCell} from "./InspectionReportCellStatus";
import "./inspectionReportMatrix.css";

const IR_OVERVIEW_CORNER_WIDTH = 184;
const IR_OVERVIEW_SELECT_COL_WIDTH = 108;

function isSelectItemType(type) {
    return parseInt(type, 10) === 1;
}

function getOverviewTableMinWidth(items) {
    const selectCount = items.filter((item) => isSelectItemType(item.type)).length;
    return IR_OVERVIEW_CORNER_WIDTH + selectCount * IR_OVERVIEW_SELECT_COL_WIDTH;
}

function isFilled(value) {
    return value !== undefined && value !== null && value !== "";
}

function InspectionReportAssetSection({
    group,
    items,
    asset,
    readOnly,
    getCellValue,
    onChange,
}) {
    const [modal, setModal] = useState(null);

    const countFilledForAsset = (assetId) =>
        items.filter(item => isFilled(getCellValue(group.groupName, assetId, item.id))).length;

    const totalCells = (asset?.length ?? 0) * items.length;
    const filledCells = asset?.reduce((sum, each) => sum + countFilledForAsset(each.id), 0) ?? 0;
    const progressPct = totalCells > 0 ? Math.round((filledCells / totalCells) * 100) : 0;
    const hasTextCols = items.some((item) => !isSelectItemType(item.type));

    const closeModal = () => setModal(null);

    const renderAssetModal = () => {
        if (!modal || modal.mode !== "asset") return null;

        const activeId = modal.assetId ?? asset?.[0]?.id;
            const activeIndex = asset?.findIndex(a => a.id === activeId) ?? 0;
            const targetAsset = asset?.find(a => a.id === activeId);
            if (!targetAsset) return null;

            const assetFilled = countFilledForAsset(targetAsset.id);
            const assetPct = items.length > 0 ? Math.round((assetFilled / items.length) * 100) : 0;

            return (
                <InspectionReportEditModal
                    open
                    onClose={closeModal}
                    title={group.groupName}
                    meta={`${activeIndex + 1} / ${asset.length} · ${targetAsset.assetName}`}
                    size="xl"
                    hideHeaderBorder
                >
                    <div className="ir-asset-modal">
                        <aside className="ir-asset-modal__nav">
                            <div className="ir-asset-modal__nav-head">자산 목록</div>
                            <div className="ir-asset-modal__nav-list">
                                {asset.map((each, index) => {
                                    const filled = countFilledForAsset(each.id);
                                    const done = items.length > 0 && filled === items.length;

                                    return (
                                        <button
                                            key={each.id ?? index}
                                            type="button"
                                            className={`ir-asset-modal__nav-item${each.id === activeId ? " is-active" : ""}${done ? " is-done" : ""}`}
                                            onClick={() => setModal({mode: "asset", assetId: each.id})}
                                        >
                                            <span className="ir-asset-modal__nav-no">{index + 1}</span>
                                            <span className="ir-asset-modal__nav-name">{each.assetName}</span>
                                            <span className="ir-asset-modal__nav-count">{filled}/{items.length}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </aside>

                        <div className="ir-asset-modal__main">
                            <div className="ir-asset-modal__summary">
                                <span className="ir-asset-modal__summary-title">{targetAsset.assetName}</span>
                                <div className="ir-asset-modal__summary-right">
                                    <span className="ir-asset-modal__summary-pct">
                                        {assetFilled}/{items.length} ({assetPct}%)
                                    </span>
                                    <div className="ir-asset-modal__summary-bar">
                                        <div className="ir-asset-modal__summary-fill" style={{width: `${assetPct}%`}} />
                                    </div>
                                </div>
                            </div>

                            <div className="ir-asset-modal__form-scroll">
                                <table className="w-full asset-detail-kv-table ir-asset-modal__form">
                                    <colgroup>
                                        <col style={{width: "32%"}} />
                                        <col style={{width: "68%"}} />
                                    </colgroup>
                                    <tbody>
                                        {items.map((item, index) => (
                                            <tr
                                                key={item.id ?? index}
                                                className={parseInt(item.type, 10) !== 1 ? "asset-detail-kv-row--auto" : ""}
                                            >
                                                <th className="fieldset-th">{item.name}</th>
                                                <td className="fieldset-td">
                                                    <InspectionReportFieldControl
                                                        type={item.type}
                                                        value={getCellValue(group.groupName, targetAsset.id, item.id)}
                                                        onChange={(v) => onChange(v, group.groupName, targetAsset.id, item.id)}
                                                        name={item.name}
                                                        variant="modal"
                                                        multiline={parseInt(item.type, 10) !== 1}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </InspectionReportEditModal>
        );
    };

    return (
        <>
            <div className="ir-section-toolbar">
                <div className="ir-section-toolbar__left">
                    <span className="ir-section-stat"><strong>{asset?.length ?? 0}</strong> 자산</span>
                    <span className="ir-section-stat"><strong>{items.length}</strong> 항목</span>
                    <span className="ir-section-stat ir-section-stat--progress">
                        입력 <strong>{filledCells}/{totalCells}</strong> ({progressPct}%)
                    </span>
                </div>
            </div>

            <InspectionReportLegend
                actions={!readOnly && (
                    <button
                        type="button"
                        className="ir-section-btn ir-section-btn--primary"
                        onClick={() => setModal({mode: "asset", assetId: asset?.[0]?.id})}
                    >
                        자산별 입력
                    </button>
                )}
            />

            <div className="ir-overview-scroll">
                <table
                    className={`ir-overview-table${readOnly ? "" : " ir-overview-table--editable"}`}
                    style={{minWidth: getOverviewTableMinWidth(items)}}
                >
                    <colgroup>
                        <col style={{width: IR_OVERVIEW_CORNER_WIDTH}} />
                        {items.map((item, index) => (
                            <col
                                key={item.id ?? index}
                                style={isSelectItemType(item.type) ? {width: IR_OVERVIEW_SELECT_COL_WIDTH} : undefined}
                            />
                        ))}
                        {!hasTextCols && <col className="ir-overview-col-fill" />}
                    </colgroup>
                    <thead>
                        <tr>
                            <th className="ir-overview-table__corner">자산 \ 항목</th>
                            {items.map((item, index) => (
                                <th
                                    key={item.id ?? index}
                                    className={`ir-overview-table__col-head${isSelectItemType(item.type) ? " ir-overview-table__col-head--select" : " ir-overview-table__col-head--text"}`}
                                >
                                    {item.name}
                                </th>
                            ))}
                            {!hasTextCols && <th className="ir-overview-table__fill" aria-hidden />}
                        </tr>
                    </thead>
                    <tbody>
                        {asset?.map((each, xIndex) => (
                            <tr key={each.id ?? xIndex}>
                                <th className="ir-overview-table__row-head">
                                    {!readOnly ? (
                                        <button
                                            type="button"
                                            className="ir-overview-table__head-btn"
                                            onClick={() => setModal({mode: "asset", assetId: each.id})}
                                            title={`${each.assetName} 자산별 입력`}
                                        >
                                            <span className="ir-overview-table__row-no">{xIndex + 1}</span>
                                            <span className="ir-overview-table__row-name">{each.assetName}</span>
                                        </button>
                                    ) : (
                                        <span className="ir-overview-table__row-label">
                                            <span className="ir-overview-table__row-no">{xIndex + 1}</span>
                                            <span className="ir-overview-table__row-name">{each.assetName}</span>
                                        </span>
                                    )}
                                </th>
                                {items.map((item, yIndex) => {
                                    const value = getCellValue(group.groupName, each.id, item.id);

                                    return (
                                        <td
                                            key={item.id ?? yIndex}
                                            className={`ir-overview-table__cell${isSelectItemType(item.type) ? " ir-overview-table__cell--select" : " ir-overview-table__cell--text"}`}
                                        >
                                            {readOnly ? (
                                                <div className="ir-overview-table__cell-readonly">
                                                    <InspectionReportOverviewCell value={value} type={item.type} />
                                                </div>
                                            ) : (
                                                <div
                                                    className={`ir-overview-table__cell-input${parseInt(item.type, 10) === 1 ? " ir-overview-table__cell-input--select" : " ir-overview-table__cell-input--text"}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <InspectionReportFieldControl
                                                        type={item.type}
                                                        value={value}
                                                        onChange={(v) => onChange(v, group.groupName, each.id, item.id)}
                                                        name={item.name}
                                                        variant="cell"
                                                        multiline={parseInt(item.type, 10) !== 1}
                                                    />
                                                </div>
                                            )}
                                        </td>
                                    );
                                })}
                                {!hasTextCols && <td className="ir-overview-table__fill" aria-hidden />}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {renderAssetModal()}
        </>
    );
}

function InspectionReportAssetItem({view, obj, status, asset, setter}) {
    const readOnly = status === "view";

    const handleReportAssetViewInputChange = (v, groupName, assetId, codeId) => {
        setter((prev) => {
            if (!prev.assetView) return prev;

            const updatedAssetView = prev.assetView.map(group => {
                if (group.groupName !== groupName) return group;

                const child = group.child ? [...group.child] : [];
                const existingIndex = child.findIndex(
                    item => item.assetId === assetId && item.codeId === codeId
                );

                if (existingIndex !== -1) {
                    child[existingIndex] = {...child[existingIndex], value: v};
                } else {
                    child.push({assetId, codeId, value: v});
                }

                return {...group, child};
            });

            return {...prev, assetView: updatedAssetView};
        });
    };

    const getCellValue = (groupName, assetId, codeId) =>
        obj?.assetView
            ?.find(g => g.groupName === groupName)
            ?.child?.find(e => e.assetId === assetId && e.codeId === codeId)?.value;

    return (
        <>
            {view && obj?.assetView?.length > 0 &&
                obj.assetView.map((group, groupIndex) => {
                    const items = obj.inspectionReportCode?.children?.filter(
                        item => item.groupName === group.groupName
                    ) ?? [];

                    return (
                        <DetailPanel
                            key={groupIndex}
                            title={group.groupName}
                            className="inspection-report-section-panel"
                        >
                            <InspectionReportAssetSection
                                group={group}
                                items={items}
                                asset={asset}
                                readOnly={readOnly}
                                getCellValue={getCellValue}
                                onChange={handleReportAssetViewInputChange}
                            />
                        </DetailPanel>
                    );
                })
            }
        </>
    );
}

export default InspectionReportAssetItem;
